import forEach from "core/src/core/util/for-each.js";
import * as gsf from "core/src/core/game-state-functions.js";
import * as csf from "../canvas-state-functions.js";
import * as trajectoryUtil from "core/src/core/geometry/trajectory/trajectory-util.js";
import {wormColors} from "core/src/core/constants.js";

import ScaledCanvasContext from "../scaled-canvas-context.js";
import { drawWormEffectBars, effectBarsExtentPx } from "../effect-bars";
import { drawVictoryAnim, drawVictoryCrash, isVictoryAnim } from "../victory-anim";
import { drawUpright } from "../arena-view";
import { getVictoryLapEvent as lapEventAt, getLapDeath, LapCrashClock } from "./lap-crash";
import { drawHeadStyle, isHeadStyle, headStyleRadius, headStyleReach } from "../head-styles";
import { spawnedAt, SPAWN_SPOTLIGHT_DURATION } from "./spawn-events.js";
import { introAlpha } from "../../shared/round-intro";

var HEAD_COLOR = "#FFB74D"; // 300 orange
var KEY_SWITCH_HEAD_COLOR = "#3388BB";
// Human players get an outward ring that pulses repeatedly from their head through
// the start countdown before fading as play begins, so you can instantly find your
// own worm in a crowd.
var PULSE_INTERVAL = 0.45;      // seconds between successive outward pulses (spawn rate)
var PULSE_LIFETIME = 1.27;     // seconds each pulse takes to expand and fade
var PULSE_LINE_WIDTH = 2;    // stroke width of an outward pulse: thin, the wash carries the weight
var PULSE_MAX_DIST = 13.1;     // how far a pulse expands, in head sizes, before it dies
var PULSE_START_GAP = 2;       // a ring is born this far (game units) outside the head's edge
var PULSE_ALPHA = 0.95;        // a ring's opacity when it is born (fades to 0 over its lifetime)
var PULSE_WASH = 0.6;          // the inward wash's opacity, relative to its ring's
// How far apart successive rings are (in head sizes): the wash behind a ring
// reaches exactly this far back, so it hands over to the next ring seamlessly.
var PULSE_SPACING = PULSE_MAX_DIST * PULSE_INTERVAL / PULSE_LIFETIME;
var SWITCH_SPOTLIGHT_DURATION = 1.5; // seconds heads pulse after a switcharoonie

// `excludeHeadPlayerIds` (optional): worm heads to skip drawing, by playerId. A
// generic hook with no knowledge of why — online play uses it to suppress the local
// player's lagged head (the online-only predicted-worm renderer draws it instead).
// `getWormRenderTime(playerId)` (optional): online adaptive delay, the render time
// to use for that worm's head (each opponent on its own latency-sized clock); falsy
// falls back to the shared frame renderTime.
// `localPlayerIds` (optional): the worms THIS client controls. The "find your
// worm" pulse is drawn only for these. Offline it's undefined (every human is
// local, so all get it); online it's your own worm(s), so opponents on other
// computers don't get your indicator.
// `introDelay(playerId)` (optional): the round-start roll call (round-intro.ts).
// Seconds into the countdown at which that player's head fades in; until then
// the head and its launch arrow are not drawn. Undefined = no roll call.
export default function WormHeadRenderer({ gameState, canvasState, players, canvas, drawTrajectories, scale=1, excludeHeadPlayerIds, getWormRenderTime, localPlayerIds, preview=false, introDelay, spectator=false, rotated=false }) {
    // Whose worm gets the "this one is yours" effects (the find-your-worm pulse, the
    // switcharoonie spotlight). Nobody's when only watching (a replay): with no local
    // players named, every human counted as local, and in a replay of an online match
    // every seat is a human, so the whole field pulsed through the countdown.
    function isLocalController(playerId) {
        if (spectator) return false;
        return !localPlayerIds || localPlayerIds.indexOf(playerId) !== -1;
    }
    var context = canvas.getContext("2d");
    var scaledContext = ScaledCanvasContext(context, scale);
    var prevRenderTime = 0; // last clock this instance drew at (see the skip in render)

    // Dynamic-layer dirty rects (see worm-body-renderer): report the PIXEL regions this
    // renderer draws so next frame's clear can erase just those, or mark the frame
    // unbounded (dynFull) for FX whose extent isn't worth bounding (arrows, pulses,
    // victory anims, debug trajectories) - those force one full clear next frame.
    function dynPushPx(x, y, r) {
        var d = canvasState.dyn;
        if (d) d.cur.push([x - r, y - r, r * 2, r * 2]);
    }
    function dynFull() {
        var d = canvasState.dyn;
        if (d) d.full = true;
    }
    // A conservative pixel radius covering the head (cosmetic styles may decorate a
    // little past their nominal radius) AND the effect-bar rings around it.
    function headExtentPx(size, headStyle) {
        return Math.max(
            headStyleRadius(size, headStyle) * scale * headStyleReach(headStyle) + 8,
            effectBarsExtentPx(size * scale)
        );
    }

    // The start-phase indicators (direction arrow, find-your-worm pulse) are drawn
    // in game-space units, so on a big map squeezed into the same on-screen area
    // they shrink into invisibility. Scale them with the map's span: 1x on a small
    // (~600) arena, growing linearly, capped so they never get comical.
    var mapSpan = Math.max((gameState.map && gameState.map.width) || 0, (gameState.map && gameState.map.height) || 0);
    var startFxScale = Math.min(2.5, Math.max(1, mapSpan / 600));

    function drawHead({ x, y, direction, size, headColor, headShape, headStyle, wormColor, blinkingStartTime, renderTime}) {
        context.save();

        if (blinkingStartTime || blinkingStartTime === 0) {

            var BLINK_DURATION = 2;
            var FADE_LOW_POINT = 0;
            var timeDiff = (renderTime - (blinkingStartTime - BLINK_DURATION/2)) % BLINK_DURATION;
            var a = (timeDiff / BLINK_DURATION) * Math.PI;
            context.globalAlpha = FADE_LOW_POINT + Math.sin(a) * (1 - FADE_LOW_POINT);
        }

        // Cosmetic head (shop): draws in place of the plain dot. Falls through to
        // the default on unknown styles so a bad value can never blank a head.
        if (isHeadStyle(headStyle) && drawHeadStyle(context, x * scale, y * scale, headStyleRadius(size, headStyle) * scale, direction, renderTime, headStyle, wormColor)) {
            context.restore();
            return;
        }
        context.fillStyle = headColor;
        if (headShape === "circle") {
            context.beginPath();
            scaledContext.arc(x, y, headStyleRadius(size), 0, 2 * Math.PI);
            context.fill();
        } else if (headShape === "square") {
            scaledContext.translate(x, y);
            context.rotate(direction - Math.PI / 2);
            var hr = headStyleRadius(size);
            scaledContext.fillRect(-hr, -hr, hr * 2, hr * 2);
        } else {
            throw Error("Unknown head shape: " + headShape);
        }
        context.restore();
    }

    // Solid grey smoke balls: FILLED discs. (They used to be stroked without
    // setting a line width, so they inherited the arrow's fat stroke, which
    // filled the ball and left a pinhole in the middle.)
    function drawDrunkBubbles({renderTime, segmentId, wormId}) {
        context.save();
        context.globalAlpha = 0.3;
        context.fillStyle = "white";

        var bMinX = Infinity, bMinY = Infinity, bMaxX = -Infinity, bMaxY = -Infinity, bMaxR = 0;
        csf.getDrunkBubbles(gameState, renderTime, segmentId, wormId).forEach(function(bubble) {
            var x = bubble.x;
            var y = bubble.y;
            var radius = bubble.radius;
            if (x < bMinX) bMinX = x;
            if (x > bMaxX) bMaxX = x;
            if (y < bMinY) bMinY = y;
            if (y > bMaxY) bMaxY = y;
            if (radius > bMaxR) bMaxR = radius;
            context.beginPath();
            scaledContext.arc(x, y, radius, 0, 2 * Math.PI);
            context.fill();
        });
        if (bMinX !== Infinity && canvasState.dyn) {
            var bPad = bMaxR * scale + 8; // stroke + antialiasing slack (big fat-worm bubbles showed edge remnants)
            canvasState.dyn.cur.push([
                bMinX * scale - bPad, bMinY * scale - bPad,
                (bMaxX - bMinX) * scale + bPad * 2, (bMaxY - bMinY) * scale + bPad * 2,
            ]);
        }

        context.restore();
    }

    function drawArrow(x, y, direction, size, color) {
        context.save();
        context.fillStyle = color;
        context.strokeStyle = color;
        context.lineWidth = scale * 4 * startFxScale;
        context.beginPath();
        scaledContext.translate(x, y);
        context.rotate(direction - Math.PI / 2);

        // Start the shaft clear of the head (cosmetic heads are drawn bigger
        // than the body) plus a gap, so the arrow points away from the worm
        // instead of spearing through it. The arrow keeps its full length: the
        // whole thing shifts outward with the start point.
        var arrowStart = headStyleRadius(size) + 7;
        var arrowBaseEndpoint = arrowStart + 25 * startFxScale;
        var arrowHeadEndpoint = arrowStart + 30 * startFxScale;
        var arrowHeadWidth = 4 * startFxScale;

        scaledContext.moveTo(0, arrowStart);
        scaledContext.lineTo(0, arrowBaseEndpoint);
        scaledContext.lineTo(-arrowHeadWidth, arrowBaseEndpoint);
        scaledContext.lineTo(0, arrowHeadEndpoint);
        scaledContext.lineTo(arrowHeadWidth, arrowBaseEndpoint);
        scaledContext.lineTo(0, arrowBaseEndpoint);
        context.closePath();
        context.fill();
        context.stroke();
        context.restore();
    }

    // The victory lap and the winner's crash on it: lap-crash.js (shared with
    // the predicted renderer). A crash mid-lap hands the celebration over to its
    // crash sequence, timed on the frame clock.
    var lapCrashElapsed = LapCrashClock();
    function getVictoryLapEvent(time) {
        return lapEventAt(gameState, time);
    }

    // "Drop-in" at round start: an outward ring pulses repeatedly from the head until
    // the game starts, then fades away. gameStartTime is when the countdown ends and
    // the worms begin moving. A new ring spawns every PULSE_INTERVAL and lives for
    // PULSE_LIFETIME, expanding and fading; since a pulse outlives the interval they
    // overlap into a steady throb. No new pulse spawns after the game starts, so the
    // last ones expand and fade away.
    function drawSpawnFx(x, y, size, color, time, gameStartTime) {
        if (time < 0) {
            return;
        }
        context.save();
        context.strokeStyle = color;
        context.lineWidth = scale * PULSE_LINE_WIDTH * startFxScale;

        var firstPulse = Math.max(0, Math.ceil((time - PULSE_LIFETIME) / PULSE_INTERVAL));
        var lastPulse = Math.floor(time / PULSE_INTERVAL);
        for (var i = firstPulse; i <= lastPulse; i++) {
            var pulseStartTime = i * PULSE_INTERVAL;
            if (pulseStartTime >= gameStartTime) {
                break; // don't begin new pulses after the game starts
            }
            dynFull(); // expanding rings: not worth bounding, full-clear next frame
            var fp = (time - pulseStartTime) / PULSE_LIFETIME; // 0..1 within a pulse
            // Born just outside the head (cosmetic heads draw bigger than the body),
            // never inside it.
            var R = headStyleRadius(size) + PULSE_START_GAP + size * PULSE_MAX_DIST * startFxScale * fp;
            var a = PULSE_ALPHA * (1 - fp);
            // A thin, strong ring with a wash of colour fading INWARD from it all the
            // way back to where the next ring follows (PULSE_SPACING), so the band
            // between two rings is one continuous wave: bright at the leading edge,
            // gone by the time the next ring takes over.
            var rPx = R * scale, inPx = Math.max(0, R - size * PULSE_SPACING * startFxScale) * scale;
            var g = context.createRadialGradient(x * scale, y * scale, inPx, x * scale, y * scale, rPx);
            g.addColorStop(0, "rgba(0,0,0,0)");
            g.addColorStop(1, color);
            context.globalAlpha = a * PULSE_WASH;
            context.fillStyle = g;
            context.beginPath();
            scaledContext.arc(x, y, R, 0, 2 * Math.PI);
            context.fill();
            context.globalAlpha = a;
            context.beginPath();
            scaledContext.arc(x, y, R, 0, 2 * Math.PI);
            context.stroke();
        }
        context.restore();
    }

    function drawTrajectory(gameState, segment) {
        var player = gsf.getPlayer(gameState, segment.playerId);
        if (!player.aiData || !player.aiData.trajectory) {
            return;
        }
        var trajPlayer = players.find(p => p.id === segment.playerId);
        if (!trajPlayer) return;
        var color = wormColors[trajPlayer.colorId];
        context.save();
        context.beginPath();
        scaledContext.translate(segment.endX, segment.endY);
        context.rotate(segment.endDirection - Math.PI / 2);
        context.strokeStyle = color;
        context.setLineDash([scale * 2, scale * 5]);
        context.lineWidth = scale * 2;
        player.aiData.trajectory.forEach(function (move) {
            var turnRadius;
            if (move.turningVelocity !== 0) {
                turnRadius = Math.abs(move.speed / move.turningVelocity);
            }
            scaledContext.moveTo(0, 0);
            var distanceTravelled = move.speed * move.duration;
            var angleTurned = move.turningVelocity * move.duration;
            if (move.turningVelocity < 0) {
                scaledContext.arc(turnRadius, 0, turnRadius, Math.PI, Math.PI + angleTurned, true);
                scaledContext.translate(-turnRadius * (Math.cos(angleTurned) - 1), -turnRadius * Math.sin(angleTurned));
                context.rotate(angleTurned);
            } else if (move.turningVelocity > 0) {
                scaledContext.arc(-turnRadius, 0, turnRadius, 0, angleTurned);
                scaledContext.translate(turnRadius * (Math.cos(angleTurned) - 1), turnRadius * Math.sin(angleTurned));
                context.rotate(angleTurned);
            } else {
                scaledContext.lineTo(0, distanceTravelled);
                scaledContext.translate(0, distanceTravelled);
            }
        });
        context.stroke();
        context.restore();
    }

    function render(renderTime) {
        // The heads share the body renderer's per-frame canvas now: that renderer clears
        // it and draws every tail EARLIER in the same renderers pass, so this one must not
        // clear - and must skip frames where the render clock didn't advance (offline
        // pause, preview throttle), exactly like the body renderer does, or it would
        // stroke its alpha FX onto an uncleared previous frame and accumulate.
        if (renderTime === prevRenderTime) {
            return;
        }
        prevRenderTime = renderTime;
        if (renderTime < canvasState.prevRenderTime) {
            csf.clearPathSegmentRenderData(canvasState);
        }

        forEach(gameState.wormPathSegments, function (segments, segmentId) {
            // A track can be transiently empty (a twin worm the host created but whose
            // authoritative path hasn't streamed from its owner yet). Skip it - later
            // reads (segments[0].playerId) would throw.
            if (!segments.length) {
                return;
            }
            // Resolve ownership from the worm's CURRENT playerId, not segments[0].playerId (only its
            // ORIGINAL owner). A switcharoonie hands a worm to a new player mid-track, so keying on the
            // first segment would exclude the wrong worm and colour the head by its old owner. Used for
            // both the exclusion here and the head colour below, so the head follows the live owner.
            var headWorm = gsf.getWorm(gameState, segments[0].wormId);
            if (!headWorm) return; // worm gone (e.g. removed); its track is now just trail history
            var ownerId = headWorm.playerId;
            if (excludeHeadPlayerIds && excludeHeadPlayerIds.indexOf(ownerId) !== -1) {
                return; // own worm (+ its mirror) is drawn by the predicted-worm renderer online
            }
            // A wall-hack MIRROR track ("<player>_<worm>#N") is handled by its own pass after this
            // loop (drawMirrorHeads), which draws its head straight off the streamed ghost segments.
            // Skip it here so the main loop's per-worm FX (spotlight, spawn pulse) don't apply to it.
            if (segmentId.indexOf("#") !== -1) {
                return;
            }
            var renderData = csf.getPathSegmentRenderData(canvasState, segmentId);

            // Per-opponent adaptive delay: draw this worm's head at its own
            // (latency-sized) render time. Falls back to the shared frame time.
            var wt = renderTime;
            if (getWormRenderTime && segments[0]) {
                var rt = getWormRenderTime(segmentId);
                if (typeof rt === "number") wt = rt;
            }
            // A Respawn round: a crashed worm's head goes with its trail (core
            // respawn.js marks the track's last segment too), no corpse left behind
            var lastSeg = segments[segments.length - 1];
            if (lastSeg.clearedAt !== undefined && lastSeg.clearedAt <= renderTime) {
                return;
            }

            while (renderData.segmentIndex < segments.length - 1 && segments[renderData.segmentIndex + 1].startTime < wt) {
                renderData.segmentIndex++;
                if (segments[renderData.segmentIndex].type === "clear") {
                    renderData.latestClearSegmentIndex = [renderData.segmentIndex];
                }
            }

            var player = players.find(p => p.id === ownerId);
            // Stale player list during a preview recreate: skip this worm rather
            // than crash (see worm-body-renderer for the same guard).
            if (!player) return;
            var playerColor = wormColors[player.colorId];
            var isHuman = player.type === "human";

            // Bubbles for a STOPPED worm run on the advancing global clock: the
            // per-worm clock freezes at the death time, which would freeze in-flight
            // bubbles over the corpse forever. Stopped = marked dead, or its track
            // ended in a death marker (a victory-lap crash marks nobody dead, but
            // the track still ends). Spawning past the death is capped inside
            // getDrunkBubbles, so this only lets the last bubbles finish and vanish.
            var bubbleWorm = gsf.getWorm(gameState, segments[0].wormId);
            var lastBubbleSeg = segments[segments.length - 1];
            var wormStopped = (bubbleWorm && bubbleWorm.alive === false) || (lastBubbleSeg && lastBubbleSeg.type === "worm_died");
            drawDrunkBubbles({
                renderTime: wormStopped ? renderTime : wt,
                segmentId,
                wormId: segments[0].wormId
            });

            var wormHeadSegment = csf.getWormHeadToRender(canvasState, gameState, segmentId, wt);
            if(wormHeadSegment) {
                // Clamp to the segment end so that when the render clock has overrun
                // the latest received segment (held by getWormHeadToRender), the head
                // pauses at the last known position instead of extrapolating past it.
                var headPosTime = Math.min(wt, wormHeadSegment.endTime);
                var position = trajectoryUtil.followTrajectory(wormHeadSegment, headPosTime - wormHeadSegment.startTime);
                // (where this head is drawn this frame: the winner's spotlight follows it)
                if (canvasState.heads) canvasState.heads[segmentId] = { x: position.x * scale, y: position.y * scale, playerId: player.id, at: renderTime };
                var size = wormHeadSegment.size;
                var headColor = HEAD_COLOR;
                var headShape = "circle";
                var wormHeadTime = Math.min(wt, wormHeadSegment.endTime);
                var switchSpotlightTime;
                // Power-ups now show as status bars on EVERY worm's head (drawn below), so the
                // head no longer changes colour (key_switch) / shape (tronTurn) or blinks
                // (wall-hack) - that just doubled up. Drunk bubbles stay (they're the effect
                // itself, not an indicator). Keep the switch_slow "find your swapped worm" pulse.
                csf.getActiveEffects(gameState, wormHeadTime, {wormId: wormHeadSegment.wormId}).forEach(function (effect) {
                    if (effect.name === "switch_slow") {
                        switchSpotlightTime = effect.time;
                    }
                });

                // Roll call: this head's fade-in (1 once the round is under way).
                var intro = introDelay ? introAlpha(wt, introDelay(player.id), gameState.startPhaseTime) : 1;
                if (intro <= 0) {
                    dynFull(); // nothing drawn yet; keep the frame clean
                    return;
                }
                var introDelayed = introDelay ? (introDelay(player.id) || 0) : 0;
                context.save();
                context.globalAlpha = intro;
                // Speed 0 decodes as still_arc, so another player's worm frozen by a
                // switcharoonie arrives looking like the start phase: the arrow only
                // belongs to a segment that began before the round got going.
                if (wormHeadSegment.type === "still_arc" && wormHeadSegment.startTime < (gameState.startPhaseTime || 0) - 1e-9) {
                    dynFull(); // launch arrow: start phase only, full-clear next frame
                    drawArrow(position.x, position.y, position.direction, size, playerColor);
                } else if (drawTrajectories) {
                    dynFull(); // debug trajectories: unbounded
                    drawTrajectory(gameState, wormHeadSegment);
                }
                drawHead({
                    x: position.x,
                    y: position.y,
                    direction: position.direction,
                    size,
                    headColor,
                    headShape,
                    headStyle: player.headStyle,
                    wormColor: playerColor,
                    blinkingStartTime: undefined, // no wall-hack blink; the bar shows it
                    renderTime: wormHeadTime
                });
                context.restore();
                // Active power-up status bars on EVERY worm's head (own + opponents).
                drawWormEffectBars(context, gameState, wormHeadSegment.wormId, position.x, position.y, size, scale, wormHeadTime, preview);
                dynPushPx(position.x * scale, position.y * scale, headExtentPx(size, player.headStyle));
                // (Wall-hack mirror heads are drawn in a separate pass below, straight from the
                // streamed "#N" ghost tracks - not guessed from this head's position.)
                // Only mark worms this client controls (online: your own, not
                // opponents on other machines; offline: every human).
                if (isHuman && isLocalController(player.id)) {
                    // The pulses start when the head appears, not before.
                    drawSpawnFx(position.x, position.y, size, playerColor, wt - introDelayed, gameState.startPhaseTime - introDelayed);
                }
                // Victory animation (cosmetic): the round winner's celebration plays at
                // their head through the victory lap - but only for players that OWN one
                // (player.victoryAnim). Humans have none by default; the shop will sell
                // them. The victory_lap game event (pushed by core's enterVictoryLap)
                // carries the winner + lap start time, locally and online alike.
                var lapEvent = getVictoryLapEvent(wt);
                if (lapEvent && lapEvent.playerId === player.id && isVictoryAnim(player.victoryAnim)) {
                    dynFull(); // celebration FX: unbounded, full-clear next frame
                    // startFxScale keeps the celebration readable on big maps,
                    // same as the round-start indicators. A crash mid-lap hands
                    // over to the celebration's crash sequence.
                    var death = getLapDeath(gameState, lapEvent, player.id, wt, segments[segments.length - 1].endTime);
                    var crashElapsed = death ? lapCrashElapsed(player.id, lapEvent, renderTime) : 0;
                    // Upright on screen even when the view is turned (arena-view.ts).
                    drawUpright(context, position.x * scale, position.y * scale, rotated, function () {
                        (death ? drawVictoryCrash : drawVictoryAnim)(
                            context,
                            position.x * scale,
                            position.y * scale,
                            size * scale * startFxScale,
                            playerColor,
                            player.flag,
                            death ? crashElapsed : wt - lapEvent.time,
                            player.victoryAnim,
                            death ? death.time - lapEvent.time : 0
                        );
                    });
                }
                // Switcharoonie spotlight: pulse the head briefly so you can find your
                // newly swapped worm. Keyed off the worm's *current* controller, since
                // a switch hands your keys to a different (differently-coloured) worm.
                if (switchSpotlightTime !== undefined) {
                    var controller = players.find(p => p.id === wormHeadSegment.playerId);
                    if (controller && controller.type === "human" && isLocalController(controller.id)) {
                        drawSpawnFx(position.x, position.y, size, playerColor, wt - switchSpotlightTime, SWITCH_SPOTLIGHT_DURATION);
                    }
                }
                // A worm put in while the round is going (back in after a crash,
                // or dropping in): the round start's pulses, so its owner finds it.
                var putInAt = spawnedAt(gameState, wormHeadSegment.wormId);
                if (putInAt !== undefined && isHuman && isLocalController(player.id)) {
                    drawSpawnFx(position.x, position.y, size, playerColor, wt - putInAt, SPAWN_SPOTLIGHT_DURATION);
                }
            }
        });
        drawMirrorHeads(renderTime);
    }

    // Wall-hack MIRROR heads. The mirror is a real, streamed thing: the "<player>_<worm>#N" ghost
    // track, created by the worm's OWNER exactly while it straddles a wall and sent on to us (same
    // as a twin's segments). Draw its head straight off those segments, like any worm head, so it
    // appears + vanishes with the crossing on every machine with no per-view position guessing. The
    // OWN worm's mirror is drawn by the predicted renderer (excluded here, just like its main head),
    // so this pass is opponents only.
    function drawMirrorHeads(renderTime) {
        forEach(gameState.wormPathSegments, function (segments, segmentId) {
            var hash = segmentId.indexOf("#");
            if (hash === -1 || !segments.length) return;
            if (excludeHeadPlayerIds && excludeHeadPlayerIds.indexOf(segments[0].playerId) !== -1) return;
            var mainId = segmentId.slice(0, hash); // the mirror shares its worm's render clock
            var wt = renderTime;
            if (getWormRenderTime) {
                var rt = getWormRenderTime(mainId);
                if (typeof rt === "number") wt = rt;
            }
            // Only while our render clock is still INSIDE the mirror track. The owner stops adding
            // segments the instant it stops straddling, so once our (delayed) clock reaches the last
            // one the crossing is visually over: stop, don't freeze a head at the tip.
            if (wt > segments[segments.length - 1].endTime) return;
            // Advance this mirror track's OWN render cursor to the segment covering wt. The main
            // loop skips "#" tracks before it does this, so without it getWormHeadToRender reads a
            // stuck index (0), finds wt past that segment but before the tip, and returns null -
            // which is why the mirror head never showed on the server / C2.
            var mrd = csf.getPathSegmentRenderData(canvasState, segmentId);
            while (mrd.segmentIndex < segments.length - 1 && segments[mrd.segmentIndex + 1].startTime < wt) {
                mrd.segmentIndex++;
            }
            var headSeg = csf.getWormHeadToRender(canvasState, gameState, segmentId, wt);
            if (!headSeg) return;
            var player = players.find(p => p.id === segments[0].playerId);
            if (!player) return;
            var t = Math.min(wt, headSeg.endTime);
            var pos = trajectoryUtil.followTrajectory(headSeg, t - headSeg.startTime);
            // Mirror head follows the same "bars, not head-appearance" rule: plain circle,
            // no key_switch colour / tronTurn square / wall-hack blink.
            drawHead({
                x: pos.x,
                y: pos.y,
                direction: pos.direction,
                size: headSeg.size,
                headColor: HEAD_COLOR,
                headShape: "circle",
                blinkingStartTime: undefined,
                renderTime: t
            });
            dynPushPx(pos.x * scale, pos.y * scale, headExtentPx(headSeg.size, null));
        });
    }

    return {
        render: render
    };
};
